import { take, call, put } from "redux-saga/effects";
import Auth from "@defi/core-auth";
import Url from "../utils/url";
import { PATH_ROOT, PATH_ROOT_DEFI, PATH_DASHBOARD, PATH_LOGOUT_FULL, PATH_LOGOUT_SSO, PATH_EXPIRED, PATH_CHANGE_PASSWORD } from "../paths";
import { LOGIN_REQUEST, SSO_LOGIN_REQUEST, VALIDATE_MFA_REQUEST, SET_MFA_CHALLENGE } from "../actions/constants";
import { SENDING_REQUEST, REQUEST_ERROR, REQUEST_SUCCESS, LOADING, CLEAR_ERROR } from "@defi/core-auth";
import { push } from "connected-react-router";

/**
 * Effect to handle authorization
 * @param  {string} username               The username of the user
 * @param  {string} password               The password of the user
 * @param  {object} options                Options
 */
export function* authorize({ username, password }) {
	// We send an action that tells Redux we're sending a request
	yield put({ type: SENDING_REQUEST, sending: true });
	yield put({ type: LOADING, payload: true });

	// We then try to log in the user
	try {
		let response;
		response = yield call(Auth.login, username, password);
		yield put({ type: LOADING, payload: false });
		return response;
	} catch (error) {
		// If we get an error we send Redux the appropiate action and return
		yield put({ type: REQUEST_ERROR, error: error.message });
		yield put({ type: LOADING, payload: false });
		return false;
	} finally {
		// When done, we tell Redux we're not in the middle of a request any more
		yield put({ type: SENDING_REQUEST, sending: false });
	}
}

export function* validateMfa({ username, password, mfacode, rememberMe }) {
	// We send an action that tells Redux we're sending a request
	yield put({ type: SENDING_REQUEST, sending: true });
	yield put({ type: LOADING, payload: true });

	// We then try to log in the user
	try {
		let response;
		response = yield call(Auth.validateMfa, username, password, mfacode, rememberMe);
		return response;
	} catch (error) {
		// If we get an error we send Redux the appropiate action and return
		yield put({ type: REQUEST_ERROR, error: error.message });
		yield put({ type: LOADING, payload: false });
		return false;
	} finally {
		// When done, we tell Redux we're not in the middle of a request any more
		yield put({ type: SENDING_REQUEST, sending: false });
	}
}

/**
 * Log in saga
 */
export function* loginFlow() {
	// Because sagas are generators, doing `while (true)` doesn't block our program
	// Basically here we say 'this saga is always listening for actions'
	while (true) {
		// And we're listening for `LOGIN_REQUEST` actions and destructuring its payload
		let request = yield take(LOGIN_REQUEST);
		let { username, password } = request.data;

		// Clear error before we try to authorize again
		yield put({ type: CLEAR_ERROR });

		// Call the authorize for username/password
		let response = yield call(authorize, { username, password });
		// Setup redirect if in url
		let redirectUrl = Url.getRedirectParameter();
		if (redirectUrl !== null) {
			if (redirectUrl.substring(0, 1) !== "/") {
				redirectUrl = "/" + redirectUrl;
			}
		}

		// Move on

		let pathRoot = localStorage.DefiUserLogin === "true" ? PATH_ROOT_DEFI : PATH_ROOT;
		let pathLogout = localStorage.SsoLoggedIn === "true" ? PATH_ROOT + PATH_LOGOUT_SSO : PATH_LOGOUT_FULL;

		if (!response) {
			yield put(push(pathRoot + (redirectUrl !== null ? "?ReturnUrl=" + redirectUrl : "")));
		} else {
			if (response.MfaChallenge) {
				const mfaText = "Please enter the verification code sent to your email";
				yield put({ type: SET_MFA_CHALLENGE, challenge: true, text: mfaText, shouldDisplay: response.MfaChallenge.NumberOfDaysDeviceMayBeRemembered !== undefined ? response.MfaChallenge.NumberOfDaysDeviceMayBeRemembered > 0 : false });
			} else if (response.Profile.ForceLogoff === true) {
				yield put(push(pathLogout + (redirectUrl !== null ? "?ReturnUrl=" + redirectUrl : "")));
			} else if (response.Profile.PasswordExpired === true) {
				yield put({ type: LOADING, payload: false });
				yield put(push(PATH_ROOT + PATH_EXPIRED + (redirectUrl !== null ? "?ReturnUrl=" + redirectUrl : "")));
			} else if (response.Profile.ForceChangePassword === true) {
				yield put({ type: LOADING, payload: false });
				yield put(push(PATH_ROOT + PATH_CHANGE_PASSWORD));
			} else {
				yield put({
					type: REQUEST_SUCCESS,
					success: "Logged in successfully",
				});
				if (redirectUrl !== null) {
					window.location = window.location.origin + redirectUrl;
				} else {
					window.location = PATH_DASHBOARD;
				}
			}
		}
	}
}

export function* ssoLoginFlow() {
	while (true) {
		let request = yield take(SSO_LOGIN_REQUEST);
		console.log("ssoLoginFlow processing SSO_LOGIN_REQUEST");

		let { success, message, response } = request.data;

		// TODO handle redirectUrl for SSO

		if (!success) {
			yield put({
				type: REQUEST_ERROR,
				error: message,
			});
		} else {
			yield put({
				type: REQUEST_SUCCESS,
				success: "Logged in successfully",
			});

			yield call(Auth.ssoLogin, response);

			let redirectUrl = Url.getRedirectUrlFromStore();
			if (redirectUrl !== null) {
				if (redirectUrl.substring(0, 1) !== "/") {
					redirectUrl = "/" + redirectUrl;
				}
				Url.removeUrlFromStorage();
			}
			if (redirectUrl !== null && redirectUrl !== "/null") {
				window.location = window.location.origin + redirectUrl;
			} else {
				window.location = PATH_DASHBOARD;
			}
		}
	}
}

export function* validateMfaCodeFlow() {
	// Because sagas are generators, doing `while (true)` doesn't block our program
	// Basically here we say 'this saga is always listening for actions'
	while (true) {
		// And we're listening for `LOGIN_REQUEST` actions and destructuring its payload
		let request = yield take(VALIDATE_MFA_REQUEST);
		let { username, password, mfacode, rememberMe } = request.data;

		// Clear error before we try to authorize again
		yield put({ type: CLEAR_ERROR });

		// Call the validateMfa for username/password/mfacode
		let response = yield call(validateMfa, { username, password, mfacode, rememberMe });

		// Setup redirect if in url
		let redirectUrl = Url.getRedirectParameter();
		if (redirectUrl !== null) {
			if (redirectUrl.substring(0, 1) !== "/") {
				redirectUrl = "/" + redirectUrl;
			}
		}

		// Move on
		if (!response) {
			yield put(push(PATH_ROOT + (redirectUrl !== null ? "?ReturnUrl=" + redirectUrl : "")));
		} else {
			if (response.Profile.ForceLogoff === true) {
				yield put(push(PATH_LOGOUT_FULL + (redirectUrl !== null ? "?ReturnUrl=" + redirectUrl : "")));
			} else if (response.Profile.PasswordExpired === true) {
				yield put({ type: LOADING, payload: false });
				yield put(push(PATH_ROOT + PATH_EXPIRED + (redirectUrl !== null ? "?ReturnUrl=" + redirectUrl : "")));
			} else if (response.Profile.ForceChangePassword === true) {
				yield put({ type: LOADING, payload: false });
				yield put(push(PATH_ROOT + PATH_CHANGE_PASSWORD));
			} else {
				if (redirectUrl !== null) {
					window.location = window.location.origin + redirectUrl;
				} else {
					window.location = PATH_DASHBOARD;
				}
			}
		}
	}
}
